import React from 'react';
import { TestTube, CheckCircle, AlertCircle, RefreshCw, Eye, EyeOff } from 'lucide-react';
import type { GrafanaConfig, ShowPasswords } from '../types';

interface MonitoringSettingsTabProps {
  config: GrafanaConfig;
  onChange: (config: GrafanaConfig) => void;
  onTest: () => void;
  testingConnection: boolean;
  testResult: string | null;
  showPasswords: ShowPasswords;
  onTogglePassword: (field: string) => void;
}

const MonitoringSettingsTab: React.FC<MonitoringSettingsTabProps> = ({
  config,
  onChange,
  onTest,
  testingConnection,
  testResult,
  showPasswords,
  onTogglePassword
}) => {
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Configuración de Monitoreo con Grafana</h2>
        <p className="text-sm text-gray-600 mb-6">
          Configura la integración con Grafana para monitorear eventos de autenticación, detectar anomalías
          y visualizar métricas del sistema en tiempo real.
        </p>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Información Importante</p>
            <p>Necesitas tener una cuenta de Grafana Cloud o una instalación local de Grafana para usar esta funcionalidad.</p>
            <p className="mt-2">
              Consulta la <strong>guía completa</strong> en: <code className="bg-blue-100 px-1 py-0.5 rounded">GRAFANA_SETUP_GUIDE.md</code>
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-center">
          <input
            type="checkbox"
            id="grafana-enabled"
            checked={config.enabled}
            onChange={(e) => onChange({ ...config, enabled: e.target.checked })}
            className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <label htmlFor="grafana-enabled" className="ml-2 text-sm font-medium text-gray-700">
            Habilitar monitoreo con Grafana
          </label>
        </div>

        {config.enabled && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">URL de Grafana</label>
              <input
                type="url"
                value={config.url}
                onChange={(e) => onChange({ ...config, url: e.target.value })}
                placeholder="https://your-org.grafana.net"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="mt-1 text-xs text-gray-500">URL de tu instancia de Grafana</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">API Key de Grafana</label>
              <div className="relative">
                <input
                  type={showPasswords['grafana-api-key'] ? 'text' : 'password'}
                  value={config.apiKey}
                  onChange={(e) => onChange({ ...config, apiKey: e.target.value })}
                  placeholder="glsa_xxxxxxxxxxxxxxxxxxxx"
                  className="w-full px-4 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => onTogglePassword('grafana-api-key')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPasswords['grafana-api-key'] ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">API Key con permisos de escritura</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">URL de Loki (Logs)</label>
              <input
                type="url"
                value={config.lokiUrl}
                onChange={(e) => onChange({ ...config, lokiUrl: e.target.value })}
                placeholder="https://logs-prod-us-central1.grafana.net"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="mt-1 text-xs text-gray-500">URL del servicio Loki para enviar logs</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Dashboard ID (Opcional)</label>
              <input
                type="text"
                value={config.dashboardId}
                onChange={(e) => onChange({ ...config, dashboardId: e.target.value })}
                placeholder="abc123def"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <p className="mt-1 text-xs text-gray-500">ID del dashboard de monitoreo en Grafana</p>
            </div>

            <div className="pt-4 border-t border-gray-200">
              <button
                onClick={onTest}
                disabled={testingConnection || !config.url || !config.apiKey}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {testingConnection ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Probando conexión...
                  </>
                ) : (
                  <>
                    <TestTube className="w-4 h-4" />
                    Probar Conexión
                  </>
                )}
              </button>

              {testResult && (
                <div className={`mt-3 p-3 rounded-lg ${testResult === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                  <div className="flex items-center gap-2">
                    {testResult === 'success' ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      <AlertCircle className="w-5 h-5" />
                    )}
                    <span className="text-sm font-medium">
                      {testResult === 'success' ? '✓ Conexión exitosa con Grafana' : `✗ ${testResult}`}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-2">Qué se monitorea:</h3>
        <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
          <li>Eventos de autenticación (login, logout, cambios de sesión)</li>
          <li>Violaciones de integridad de sesión</li>
          <li>Cambios de rol no autorizados</li>
          <li>Detección de despliegues</li>
          <li>Anomalías de seguridad</li>
        </ul>
        <p className="mt-3 text-xs text-gray-600">
          Todos los eventos también se registran en la base de datos (tabla <code className="bg-gray-200 px-1 rounded">auth_audit_log</code>)
          independientemente de la configuración de Grafana.
        </p>
      </div>
    </div>
  );
};

export default MonitoringSettingsTab;
